import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Loader, Plus } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { usePromptStore } from '../../store/promptStore';
import ImportPrompts from './ImportPrompts';

export default function PromptAdminDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading, checkAuth } = useAuthStore();
  const { 
    prompts, 
    categories, 
    tags, 
    loading, 
    error,
    fetchPrompts,
    fetchCategories,
    fetchTags
  } = usePromptStore();
  const [initialized, setInitialized] = useState(false);

  // Check authentication
  useEffect(() => {
    checkAuth();
    
    // Redirect to login if not authenticated
    if (!authLoading && !user) {
      navigate('/prompt-admin/login');
    }
  }, [checkAuth]);

  // Fetch data when component mounts
  useEffect(() => {
    const initializeData = async () => {
      if (user && !initialized) {
        await Promise.all([
          fetchPrompts(),
          fetchCategories(),
          fetchTags()
        ]);
        setInitialized(true);
      }
    };

    initializeData();
  }, [user, initialized, fetchPrompts, fetchCategories, fetchTags]);

  if (authLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader className="h-8 w-8 text-[#F98B3D] animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      null
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Prompt Library Admin</h1>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Logged in as {user.email}
              </span>
              <button
                onClick={() => navigate('/prompt-library')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                View Public Library
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Stats Cards */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-2">Prompts</h2>
            <p className="text-3xl font-bold text-[#F98B3D]">{prompts.length}</p>
            <div className="mt-4">
              <button
                onClick={() => navigate('/prompt-admin/prompts/new')}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add New
              </button>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-2">Categories</h2>
            <p className="text-3xl font-bold text-[#F98B3D]">{categories.length}</p>
            <div className="mt-4">
              <button
                onClick={() => navigate('/prompt-admin/categories')}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
              >
                <Plus className="h-4 w-4 mr-1" />
                Manage
              </button>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-2">Tags</h2>
            <p className="text-3xl font-bold text-[#F98B3D]">{tags.length}</p>
            <div className="mt-4">
              <button
                onClick={() => navigate('/prompt-admin/tags')}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
              >
                <Plus className="h-4 w-4 mr-1" />
                Manage
              </button>
            </div>
          </div>
        </div>

        {/* Recent Prompts */}
        <div className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">Recent Prompts</h2>
            <button
              onClick={() => navigate('/prompt-admin/prompts')}
              className="text-[#F98B3D] hover:text-[#e07a2c]"
            >
              View All
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-32">
              <Loader className="h-8 w-8 text-[#F98B3D] animate-spin" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
              <p className="text-red-700">{error}</p>
            </div>
          ) : prompts.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-6 text-center">
              <p className="text-gray-500 mb-4">No prompts found. Get started by adding your first prompt.</p>
              <button
                onClick={() => navigate('/prompt-admin/prompts/new')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add New Prompt
              </button>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {prompts.slice(0, 5).map((prompt) => (
                  <li key={prompt.id}>
                    <div className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/prompt-admin/prompts/${prompt.id}`)}>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-[#F98B3D] truncate">{prompt.title}</p>
                        <div className="ml-2 flex-shrink-0 flex">
                          <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            {prompt.prompt_categories?.name || 'Uncategorized'}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 sm:flex sm:justify-between">
                        <div className="sm:flex">
                          <p className="flex items-center text-sm text-gray-500">
                            {prompt.description}
                          </p>
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                          <p>
                            Updated {new Date(prompt.updated_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Import Prompts Section */}
        <div className="mt-8">
          <ImportPrompts />
        </div>
      </main>
    </div>
  );
}